// @ts-nocheck
// ============================================
// 快捷键管理Hook
// 功能：统一管理应用的快捷键
// ============================================

import { useEffect, useCallback } from 'react';

/**
 * 快捷键配置
 */
interface ShortcutDef {
  key: string;
  ctrl?: boolean;
  description: string;
}

const SHORTCUTS: Record<string, ShortcutDef> = {
  SAVE_SESSION: { key: 's', ctrl: true, description: '保存当前会话' },
  CLOSE_DIALOG: { key: 'Escape', description: '关闭弹窗' },
  CONFIRM: { key: 'Enter', description: '确认操作' },
  SEARCH: { key: 'f', ctrl: true, description: '搜索标注' },
  NEXT_PAGE: { key: 'ArrowDown', description: '下一页' },
  PREV_PAGE: { key: 'ArrowUp', description: '上一页' },
  NEXT_PAGE_SPACE: { key: ' ', description: '下一页(空格)' },
  UNDO: { key: 'z', ctrl: true, description: '撤销删除' },
};

export type ShortcutAction = keyof typeof SHORTCUTS;

/**
 * 检查快捷键是否匹配
 */
const isShortcutMatch = (event: KeyboardEvent, shortcut: ShortcutDef): boolean => {
  // 检查ctrl/cmd键
  const ctrlMatch = shortcut.ctrl ? (event.ctrlKey || event.metaKey) : true;

  // 检查是否有额外的修饰键要求
  if (shortcut.ctrl && !ctrlMatch) {
    return false;
  }

  // 如果不要求ctrl但按下了ctrl，某些情况下也不匹配（除非明确允许）
  if (!shortcut.ctrl && (event.ctrlKey || event.metaKey) && shortcut.key !== 'Escape') {
    return false;
  }

  // 检查按键
  const keyMatch = event.key === shortcut.key;

  return ctrlMatch && keyMatch;
};

type ShortcutHandlers = Partial<Record<string, (event: KeyboardEvent) => void>>;

interface KeyboardShortcutsOptions {
  enabled?: boolean;
  preventDefault?: boolean;
  excludeInputs?: boolean;
}

/**
 * 快捷键管理Hook
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers = {}, options: KeyboardShortcutsOptions = {}) {
  const {
    enabled = true,
    preventDefault = true,
    excludeInputs = true, // 在输入框中禁用
  } = options;

  // 处理键盘事件
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // 排除输入框
      if (excludeInputs) {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        const isInput = tagName === 'input' || tagName === 'textarea';
        const isContentEditable = target.isContentEditable;

        if (isInput || isContentEditable) {
          // 只允许ESC键在输入框中工作
          if (event.key !== 'Escape') {
            return;
          }
        }
      }

      // 检查所有快捷键
      for (const [actionKey, shortcut] of Object.entries(SHORTCUTS)) {
        if (isShortcutMatch(event, shortcut)) {
          const handler = handlers[actionKey];

          if (handler && typeof handler === 'function') {
            if (preventDefault) {
              event.preventDefault();
            }

            handler(event);
            break;
          }
        }
      }
    },
    [enabled, handlers, preventDefault, excludeInputs]
  );

  // 注册/注销事件监听
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  // 返回工具函数
  return {
    shortcuts: SHORTCUTS,
    getShortcutDescription: (actionKey: string): string => {
      const shortcut = SHORTCUTS[actionKey];
      if (!shortcut) return '';

      let desc = '';
      if (shortcut.ctrl) desc += 'Ctrl+';
      desc += shortcut.key === ' ' ? 'Space' : shortcut.key;

      return desc;
    },
  };
}

interface ShortcutListItem {
  key: string;
  keys: string;
  description: string;
}

/**
 * 快捷键帮助面板Hook
 */
export function useShortcutHelp() {
  const getShortcutsList = useCallback((): ShortcutListItem[] => {
    return Object.entries(SHORTCUTS).map(([key, shortcut]) => {
      let keys = '';
      if (shortcut.ctrl) keys += 'Ctrl + ';
      keys += shortcut.key === ' ' ? 'Space' : shortcut.key;

      return {
        key,
        keys,
        description: shortcut.description,
      };
    });
  }, []);

  return {
    shortcuts: getShortcutsList(),
  };
}

export default useKeyboardShortcuts;
